import { APPROVAL, parseMaskPoints } from './findingsMetrics.js';
import { viewerInspectsFor } from './reportFormat.js';

const finite = value => value !== null && value !== '' && value !== undefined && Number.isFinite(Number(value));
export function hasSpatialPoint(point) {
  return !!point && ['x', 'y', 'z'].every(key => finite(point[key]));
}

function withCentre(finding) {
  if (finite(finding.imageXCenterCoordinate) && finite(finding.imageYCenterCoordinate)) return finding;
  const mask = parseMaskPoints(finding.maskPoints);
  if (mask.length < 3) return null;
  return {
    ...finding,
    imageXCenterCoordinate: mask.reduce((sum, p) => sum + p.x, 0) / mask.length,
    imageYCenterCoordinate: mask.reduce((sum, p) => sum + p.y, 0) / mask.length,
  };
}

// The current page owns the markers. Related evidence belongs to the active row only.
export function buildVerifiedViewerInspects(entries, detail, imageId, activeId) {
  const rows = (entries || []).filter(f => f && f.approvalStatus === APPROVAL.ACCEPTED);
  const byId = new Map();
  rows.forEach((row, index) => {
    const source = detail && detail.id === row.id && detail.approvalStatus === APPROVAL.ACCEPTED ? detail : row;
    // The shared viewer interpolates badge labels as HTML, so labels must be numeric.
    const referenceNumber = String(row.friendlyId || '').match(/(?:^|-)0*(\d+)$/);
    const badgeIndex = referenceNumber ? referenceNumber[1] : String(index + 1);
    byId.set(row.id, { ...source, children: [], ownerId: row.id, badgeIndex });
  });
  if (detail && detail.id === activeId && byId.has(detail.id) && detail.approvalStatus === APPROVAL.ACCEPTED) {
    (detail.children || []).filter(f => f && f.approvalStatus === APPROVAL.ACCEPTED).forEach(child => {
      if (!byId.has(child.id)) byId.set(child.id, { ...child, children: [], ownerId: detail.id, badgeIndex: byId.get(detail.id).badgeIndex });
    });
  }
  const visible = [...byId.values()].filter(f => f.imageId === imageId).map(withCentre).filter(Boolean);
  return viewerInspectsFor(visible, imageId, null).map(f => ({
    ...f,
    isActive: f.ownerId === activeId,
    strokeWidth: f.ownerId === activeId ? '5px' : '3px',
  }));
}

// Closing, navigating or choosing another row invalidates every pending completion.
export function createAnomalyDetailLoader({ load, apply, busy, failed }) {
  let revision = 0;
  return {
    invalidate() { revision += 1; busy(false); },
    async open(row) {
      const request = ++revision;
      busy(true);
      try {
        const detail = await load(row.id);
        if (request !== revision) return null;
        if (!detail || detail.id !== row.id || detail.approvalStatus !== APPROVAL.ACCEPTED) {
          throw new Error('This anomaly is no longer available as verified evidence. Refresh the list.');
        }
        const accepted = { ...detail, children: (detail.children || []).filter(f => f && f.approvalStatus === APPROVAL.ACCEPTED) };
        apply(accepted);
        return accepted;
      } catch (err) {
        if (request === revision) failed(err);
        return null;
      } finally {
        if (request === revision) busy(false);
      }
    },
  };
}
